import React, { useCallback, useEffect, useState } from "react";
import { BusinessVatConfigurationForm } from "../../../../../../components/form/business/vatconfiguration";
import { useParams } from "react-router-dom";
import { useGetBusiness } from "../../../../../../hooks/api/business/useGetBusiness";

import { usePatchAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePatchAdditionalBusinessDetails";
import WsSnackbar, { WsSnackbarTypes } from "../../../../../../components/ws-snackbar";
import { Feedback } from "../../../../../../@types/enums/general/Feedback";
import { Stack } from "@vapor/react-material";
import { useTranslation } from "@onefront/react-sdk";

export const BusinessEditVatConfiguration = ({ isEditing, setIsEditing, setLoadingSection, loading }) => {
    const { t } = useTranslation();
    const { workspaceId, businessId } = useParams();
    const [bodyRequest, setBodyRequest] = useState<any>({});
    const [feedback, setFeedback] = useState<any>(null);
    const [errors, setErrors] = useState<any>({});
    const [displayErrors, setDisplayErrors] = useState(false);

    const { data: business, loading: loadingGet, fetch: getBusiness } = useGetBusiness(workspaceId, businessId);
    const { loading: loadingPatchVatConfiguration, fetch: patchBusinessVatConfiguration } =
        usePatchAdditionalBusinessDetails(workspaceId, businessId, "vat-configuration", business?.vatConfiguration?.id);

    const handleChange = (val, key) => {
        setBodyRequest(prev => ({
            ...prev,
            [key]: val
        }));
    };

    const handleSave = useCallback(() => {
        const { id } = business?.vatConfiguration ?? {};

        if (Object.keys(errors).some(key => errors[key])) {
            setDisplayErrors(errors);
        }

        if (id) {
            const { vatExemptionForExporterStartingDate, vatExemptionManagementForExporter, ...rest } = bodyRequest;
            //TODO: remove control on vatExemptionManagementForExporter === "MOBILE_PLAFOND" to send vatExemptionForExporterStartingDate when vatExemptionForExporterStartingDate can be null otherwise.
            patchBusinessVatConfiguration({
                url: `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/business/${businessId}/vat-configuration/${id}`,
                data: {
                    ...rest,
                    vatExemptionManagementForExporter,
                    ...(vatExemptionManagementForExporter === "MOBILE_PLAFOND" && {
                        vatExemptionForExporterStartingDate
                    })
                }
            })
                .then(res => {
                    setIsEditing(false);
                    getBusiness?.().then(res => {
                        const { vatConfiguration } = res?.data ?? {};
                        if (vatConfiguration) {
                            setBodyRequest(prev => ({
                                ...prev,
                                ...vatConfiguration,
                                id: undefined
                            }));
                        }
                    });
                    setFeedback(Feedback.SUCCESS);
                })
                .catch(err => {
                    setFeedback(Feedback.ERROR);
                });
        }
    }, [business, bodyRequest, getBusiness, patchBusinessVatConfiguration, workspaceId, businessId, setIsEditing]);

    useEffect(() => {
        if (!loadingGet && !business) {
            getBusiness?.().then(res => {
                const { vatConfiguration } = res?.data ?? {};
                if (vatConfiguration) {
                    setBodyRequest(prev => ({
                        ...prev,
                        ...vatConfiguration,
                        id: undefined
                    }));
                }
            });
        }
    }, [businessId, getBusiness, business, loadingGet]);

    useEffect(() => {
        setLoadingSection(loadingGet);
    }, [loadingGet, setLoadingSection]);

    return (
        <Stack position={"relative"}>
            {feedback && (
                <WsSnackbar
                    position={"absolute"}
                    sx={{
                        right: "0 !important",
                        top: "-4.5rem !important",
                        width: 700
                    }}
                    type={feedback === Feedback.ERROR ? WsSnackbarTypes.error : WsSnackbarTypes.success}
                    title={t(`business.vatconfiguration.title`)}
                    message={t(`feedback.business.edit.${feedback}`)}
                    horizontal="right"
                    handleClose={() => setFeedback(null)}
                />
            )}
            <BusinessVatConfigurationForm
                bodyRequest={bodyRequest}
                handleChange={handleChange}
                isEditing={isEditing}
                onSave={handleSave}
                onCancel={() => {
                    setIsEditing(false);
                    setBodyRequest(business?.vatConfiguration);
                }}
                onError={(key, error) => {
                    setErrors(prev => ({
                        ...prev,
                        [key]: error
                    }));
                }}
                displayErrors={displayErrors}
                setDisplayErrors={setDisplayErrors}
                isNewBusiness={false}
                loading={loading || loadingGet || loadingPatchVatConfiguration}
            />
        </Stack>
    );
};
